import type { TableHandle } from '../../types/table';
import type { ApiRecord } from '../../types/api';
import React, { useRef, useState } from 'react';
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useHistory } from 'react-router-dom';

import {
    GridItem,
    CustomMaterialTable,
    SnackbarContent,
} from '../../components';
import psono_server from '../../services/api-server';
import i18n from '../../i18n';
import store from '../../services/store';

import TwoFactorChartCard from '../../containers/ChartCard/two_factor';
import BrowserChartCard from '../../containers/ChartCard/browser';
import OsChartCard from '../../containers/ChartCard/os';
import DeviceChartCard from '../../containers/ChartCard/device';
import DeleteConfirmDialog from '../../components/Dialog/DeleteConfirmDialog';
import CustomTabs from '../../components/CustomTabs/CustomTabs';
import Person from '@mui/icons-material/Person';
import Edit from '@mui/icons-material/Edit';
import CheckBox from '@mui/icons-material/CheckBox';
import NotInterested from '@mui/icons-material/NotInterested';
import Delete from '@mui/icons-material/Delete';
import Add from '@mui/icons-material/Add';
import DevicesOther from '@mui/icons-material/DevicesOther';
import {
    hasAnyScopeCapability,
    hasCapabilityForTenantIds,
} from '../../services/authorization';
import { apiErrorCode, isApiError } from '../../services/api-error';

export interface UsersViewProps {}
const UsersView = (props: UsersViewProps) => {
    const { t } = useTranslation();
    const userTableRef = useRef<TableHandle | null>(null);
    const sessionTableRef = useRef<TableHandle | null>(null);
    const history = useHistory();
    const [deleteUsers, setDeleteUsers] = useState<ApiRecord[]>([]);
    const [confirmSharedDelete, setConfirmSharedDelete] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const authorization = store.getState().user.authorization;
    const canCreateUsers = hasAnyScopeCapability(authorization, 'users.create');
    const canDeleteSessions = hasAnyScopeCapability(
        authorization,
        'users.sessions.delete'
    );
    const canReadSessions = hasAnyScopeCapability(
        authorization,
        'users.sessions.read'
    );
    const canReadStatistics = hasAnyScopeCapability(
        authorization,
        'statistics.read'
    );

    const canForUser = (code: any, user: any) =>
        hasCapabilityForTenantIds(authorization, code, user.tenant_ids);

    const onDeleteUsers = (
        selected_users: any,
        confirmSharedOwnership = false
    ) => {
        setDeleteError('');
        return Promise.all(
            selected_users.map((user: any) =>
                psono_server.admin_delete_user(
                    store.getState().user.token,
                    store.getState().user.session_secret_key,
                    user.id,
                    confirmSharedOwnership
                )
            )
        )
            .then(() => {
                userTableRef.current && userTableRef.current.onQueryChange();
            })
            .catch((error) => {
                if (
                    !confirmSharedOwnership &&
                    isApiError(error, 'SHARED_OWNERSHIP_CONFIRMATION_REQUIRED')
                ) {
                    setDeleteUsers(selected_users);
                    setConfirmSharedDelete(true);
                    return;
                }
                setDeleteError(apiErrorCode(error));
            });
    };

    const updateUsers = (selected_users: any, is_active: any) => {
        selected_users.forEach((user: any) => {
            psono_server
                .admin_update_user(
                    store.getState().user.token,
                    store.getState().user.session_secret_key,
                    user.id,
                    undefined,
                    is_active,
                    undefined,
                    undefined
                )
                .then(() => {
                    userTableRef.current &&
                        userTableRef.current.onQueryChange();
                });
        });
    };

    const onActivateUsers = (selected_users: any) => {
        return updateUsers(selected_users, true);
    };

    const onDeactivateUsers = (selected_users: any) => {
        return updateUsers(selected_users, false);
    };

    const onEditUser = (selected_users: any) => {
        history.push('/user/' + selected_users[0].id);
    };

    const onDeleteSessions = (selected_sessions: any) => {
        selected_sessions.forEach((session: any) => {
            psono_server
                .admin_delete_session(
                    store.getState().user.token,
                    store.getState().user.session_secret_key,
                    session.id
                )
                .then(() => {
                    sessionTableRef.current &&
                        sessionTableRef.current.onQueryChange();
                });
        });
    };

    const onCreateUser = () => {
        history.push('/users/create/');
    };

    const loadUsers = (query: any) => {
        const params: Record<string, string | number> = {
            page_size: query.pageSize,
            search: query.search,
            page: query.page,
        };
        if (query.orderBy) {
            if (query.orderDirection === 'asc') {
                params['ordering'] = query.orderBy.field;
            } else {
                params['ordering'] = '-' + query.orderBy.field;
            }
        }

        return psono_server
            .admin_user(
                store.getState().user.token,
                store.getState().user.session_secret_key,
                undefined,
                params
            )
            .then((response) => {
                const { users } = response.data;

                users.forEach((u: any) => {
                    u.create_date = moment(u.create_date).format(
                        'YYYY-MM-DD HH:mm:ss'
                    );
                    u.last_login = moment(u.last_login).format(
                        'YYYY-MM-DD HH:mm:ss'
                    );
                    u.is_active = u.is_active ? i18n.t('YES') : i18n.t('NO');
                    u.is_email_active = u.is_email_active
                        ? i18n.t('YES')
                        : i18n.t('NO');
                    u.yubikey_otp_enabled = u.yubikey_otp_enabled
                        ? i18n.t('YES')
                        : i18n.t('NO');
                    u.google_authenticator_enabled =
                        u.google_authenticator_enabled
                            ? i18n.t('YES')
                            : i18n.t('NO');
                    u.duo_enabled = u.duo_enabled
                        ? i18n.t('YES')
                        : i18n.t('NO');
                    u.webauthn_enabled = u.webauthn_enabled
                        ? i18n.t('YES')
                        : i18n.t('NO');
                });
                return {
                    data: users,
                    page: query.page,
                    pageSize: query.pageSize,
                    totalCount: response.data.count,
                };
            });
    };

    const loadSessions = (query: any) => {
        const params: Record<string, string | number> = {
            page_size: query.pageSize,
            search: query.search,
            page: query.page,
        };
        if (query.orderBy) {
            if (query.orderDirection === 'asc') {
                params['ordering'] = query.orderBy.field;
            } else {
                params['ordering'] = '-' + query.orderBy.field;
            }
        }
        return psono_server
            .admin_session(
                store.getState().user.token,
                store.getState().user.session_secret_key,
                params
            )
            .then((response) => {
                const { sessions } = response.data;

                sessions.forEach((u: any) => {
                    u.create_date = moment(u.create_date).format(
                        'YYYY-MM-DD HH:mm:ss'
                    );
                    u.valid_till = moment(u.valid_till).format(
                        'YYYY-MM-DD HH:mm:ss'
                    );
                    u.active =
                        u.active && moment(u.valid_till) > moment()
                            ? t('YES')
                            : t('NO');
                    u.completely_activated = u.active ? t('YES') : t('NO');
                });
                return {
                    data: sessions,
                    page: query.page,
                    pageSize: query.pageSize,
                    totalCount: response.data.count,
                };
            });
    };

    return (
        <div>
            {canReadStatistics && (
                <Grid container>
                    <GridItem xs={12} sm={6} md={6} lg={3}>
                        <OsChartCard />
                    </GridItem>
                    <GridItem xs={12} sm={6} md={6} lg={3}>
                        <DeviceChartCard />
                    </GridItem>
                    <GridItem xs={12} sm={6} md={6} lg={3}>
                        <BrowserChartCard />
                    </GridItem>
                    <GridItem xs={12} sm={6} md={6} lg={3}>
                        <TwoFactorChartCard />
                    </GridItem>
                </Grid>
            )}
            <Grid container>
                <GridItem xs={12} sm={12} md={12}>
                    {deleteUsers.length > 0 && (
                        <DeleteConfirmDialog
                            title={t('DELETE_USER_S')}
                            onConfirm={() => {
                                const users = deleteUsers;
                                setDeleteUsers([]);
                                const confirm = confirmSharedDelete;
                                setConfirmSharedDelete(false);
                                onDeleteUsers(users, confirm);
                            }}
                            onAbort={() => {
                                setDeleteUsers([]);
                                setConfirmSharedDelete(false);
                            }}
                        >
                            {t(
                                confirmSharedDelete
                                    ? 'DELETE_SHARED_USER_CONFIRM_DIALOG'
                                    : 'DELETE_USER_CONFIRM_DIALOG'
                            )}
                        </DeleteConfirmDialog>
                    )}
                    {deleteError && (
                        <SnackbarContent
                            message={t(deleteError)}
                            color="danger"
                        />
                    )}
                    <CustomTabs
                        title={t('USER_MANAGEMENT')}
                        headerColor="primary"
                        tabs={[
                            {
                                tabName: t('USERS'),
                                tabIcon: Person,
                                tabContent: (
                                    <CustomMaterialTable
                                        tableRef={userTableRef}
                                        columns={[
                                            {
                                                field: 'username',
                                                title: t('USERNAME'),
                                            },
                                            {
                                                field: 'create_date',
                                                title: t('CREATED_AT'),
                                            },
                                            {
                                                field: 'last_login',
                                                title: t('LAST_LOGIN'),
                                            },
                                            {
                                                field: 'is_active',
                                                title: t('ACTIVE'),
                                            },
                                            {
                                                field: 'is_email_active',
                                                title: t('EMAIL_ACTIVE'),
                                            },
                                            {
                                                field: 'yubikey_otp_enabled',
                                                title: t('YUBIKEY'),
                                            },
                                            {
                                                field: 'google_authenticator_enabled',
                                                title: t('TOTP'),
                                            },
                                            {
                                                field: 'duo_enabled',
                                                title: t('DUO_AUTHENTICATION'),
                                            },
                                            {
                                                field: 'webauthn_enabled',
                                                title: t('WEBAUTHN'),
                                            },
                                            {
                                                field: 'secret_count',
                                                title: t('SECRETS'),
                                            },
                                        ]}
                                        data={loadUsers}
                                        title={''}
                                        actions={[
                                            (data: any) => ({
                                                tooltip: canForUser(
                                                    'users.update',
                                                    data
                                                )
                                                    ? t('EDIT_USER_S')
                                                    : t('DETAILS'),

                                                icon: Edit,
                                                onClick: (evt: any) =>
                                                    onEditUser([data]),
                                            }),
                                            (data: any) => ({
                                                tooltip: t('ACTIVATE_USER_S'),
                                                icon: CheckBox,

                                                hidden: !canForUser(
                                                    'users.update',
                                                    data
                                                ),

                                                onClick: () =>
                                                    onActivateUsers([data]),
                                            }),
                                            (data: any) => ({
                                                tooltip: t('DEACTIVATE_USER_S'),
                                                icon: NotInterested,

                                                hidden: !canForUser(
                                                    'users.update',
                                                    data
                                                ),

                                                onClick: () =>
                                                    onDeactivateUsers([data]),
                                            }),
                                            (data: any) => ({
                                                tooltip: t('DELETE_USER_S'),
                                                icon: Delete,

                                                hidden: !canForUser(
                                                    'users.delete',
                                                    data
                                                ),

                                                onClick: () => {
                                                    setDeleteUsers([data]);
                                                },
                                            }),
                                            canCreateUsers && {
                                                tooltip: t('CREATE_USER'),
                                                isFreeAction: true,
                                                icon: Add,
                                                onClick: (evt: any) =>
                                                    onCreateUser(),
                                            },
                                        ].filter(Boolean)}
                                    />
                                ),
                            },
                            canReadSessions && {
                                tabName: t('SESSIONS'),
                                tabIcon: DevicesOther,
                                tabContent: (
                                    <CustomMaterialTable
                                        tableRef={sessionTableRef}
                                        columns={[
                                            {
                                                field: 'username',
                                                title: t('USERNAME'),
                                            },
                                            {
                                                field: 'create_date',
                                                title: t('LOGGED_IN_AT'),
                                            },
                                            {
                                                field: 'valid_till',
                                                title: t('VALID_TILL'),
                                            },
                                            {
                                                field: 'device_description',
                                                title: t('DEVICE_DESCRIPTION'),
                                            },
                                            {
                                                field: 'device_fingerprint',
                                                title: t('DEVICE'),
                                            },
                                            {
                                                field: 'completely_activated',
                                                title: t('ACTIVATED'),
                                            },
                                            {
                                                field: 'active',
                                                title: t('STILL_ACTIVE'),
                                            },
                                        ]}
                                        data={loadSessions}
                                        title={''}
                                        actions={[
                                            canDeleteSessions && {
                                                tooltip: t('DELETE_SESSION_S'),
                                                icon: Delete,
                                                onClick: (
                                                    evt: any,
                                                    data: any
                                                ) => onDeleteSessions([data]),
                                            },
                                        ].filter(Boolean)}
                                    />
                                ),
                            },
                        ].filter(Boolean)}
                    />
                </GridItem>
            </Grid>
        </div>
    );
};

export default UsersView;
